import { useState, useCallback } from "react";
import { DataStudioJobTypeEnum } from "~/graphql/supergraphEnums";
import useDataStudioUploadFiles from "~/hooks/useDataStudioUploadFiles/useDataStudioUploadFiles";
import { ArenaLockAction } from "../constants";
import { buildArenaLockUnlockJobConfig } from "../utils";

// Hook for creating onboarding jobs using Data Studio (using shared hook)
export const useCreateOnboardingJob = () => {
  const { createJob: createJobShared, loading } = useDataStudioUploadFiles();
  const [error, setError] = useState<string | null>(null);

  const createJob = useCallback(
    async (
      arenaDomain: string,
      arenaUserEmail: string,
      action: ArenaLockAction,
      tsvFileS3Key: string,
      customerName?: string,
      env: string = "test",
    ): Promise<{
      id: string;
      s3Uri: string;
      jobType: string;
      arenaDomain: string;
      message?: string;
    } | null> => {
      if (!arenaDomain?.trim()) {
        setError("Arena domain is required");
        return null;
      }
      if (!arenaUserEmail?.trim()) {
        setError("Arena user email is required");
        return null;
      }
      if (!tsvFileS3Key?.trim()) {
        setError("TSV file S3 key is required");
        return null;
      }
      if (![ArenaLockAction.LOCK, ArenaLockAction.UNLOCK].includes(action)) {
        setError('Action must be either "lock" or "unlock"');
        return null;
      }

      setError(null);

      // Normalize values to lowercase for backend API (consistent with Arena Creation)
      const normalizedArenaDomain = arenaDomain.toLowerCase();
      const normalizedArenaName = (customerName || arenaDomain).toLowerCase();
      const normalizedEmail = arenaUserEmail.toLowerCase();

      try {
        const jobConfig = buildArenaLockUnlockJobConfig({
          action,
          tsvFileS3Key,
          arenaUserEmail: normalizedEmail,
          arenaDomain: normalizedArenaDomain,
        });

        const result = await createJobShared({
          jobType:
            action === ArenaLockAction.LOCK
              ? DataStudioJobTypeEnum.ArenaLock
              : DataStudioJobTypeEnum.ArenaUnlock,
          arenaDomain: normalizedArenaDomain,
          arenaName: normalizedArenaName,
          arenaUserEmail: normalizedEmail,
          env,
          jobConfig,
        });

        if (!result.success) {
          setError(result.message || "Failed to create job");
          return null;
        }

        return {
          id: result.jobId || "",
          s3Uri: result.s3Uri || "",
          jobType: action === ArenaLockAction.LOCK ? "ARENA_LOCK" : "ARENA_UNLOCK",
          arenaDomain: normalizedArenaDomain,
          message: result.message,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create onboarding job";
        setError(errorMessage);
        return null;
      }
    },
    [createJobShared],
  );

  return {
    createJob,
    loading,
    error,
    clearError: () => setError(null),
  };
};
